import React, { useEffect, useState } from 'react';
import { bool, node, number, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../../util/reactIntl';
import {
  IconDownload,
  InlineTextButton,
  Modal,
  PrimaryButton,
  SecondaryButton,
} from '../../../components';
import { getExportStatusFromUrl } from '../../../util/listing';
import { useExportListings } from '../../../hooks/useExportListings';

import css from './MainPanelHeader.module.css';

const MainPanelHeader = props => {
  const {
    rootClassName,
    className,
    children,
    sortByComponent,
    isSortByActive,
    resultsCount,
    searchInProgress,
    noResultsInfo,
    title,
    description,
    showExportButton,
    listingsToExport,
    onManageDisableScrolling,
    intl,
    pagination,
    sellAsIsTabs,
    tableViewSection,
    tableSearch,
    isTableView,
    isAuthenticated,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const [isModalOpen, setModalOpen] = useState(false);

  const [modalMessage, setModalMessage] = useState('');

  const { exportListings } = useExportListings();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const message = getExportStatusFromUrl(window.location.search);
      setModalMessage(message);
    }
  }, []);

  return (
    <div className={classes}>
      {title && description && (
        <div className={css.header}>
          <span>
            <p className={css.title}>{title}</p>
            <p className={css.description}>{description}</p>
          </span>

          {showExportButton && !isTableView && isAuthenticated && (
            <SecondaryButton type="button" onClick={() => setModalOpen(true)}>
              <FormattedMessage id="General.export" />
            </SecondaryButton>
          )}
        </div>
      )}

      {sellAsIsTabs}

      {isModalOpen && (
        <Modal
          id="ExportModal"
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.modalTitle}>
            <FormattedMessage id="General.export" />
          </div>
          <p className={css.modalDescription}>
            {intl.formatMessage({ id: `MainPanelHeader.ExportModal.${modalMessage}` })}
          </p>

          <div className={css.btns}>
            <InlineTextButton onClick={() => setModalOpen(false)}>
              <FormattedMessage id="General.cancel" />
            </InlineTextButton>
            <PrimaryButton
              className={css.confirmButton}
              onClick={async () => {
                await exportListings({ listingsToExport, pagination });
                setModalOpen(false);
              }}
            >
              <FormattedMessage id="General.confirm" />
            </PrimaryButton>
          </div>
        </Modal>
      )}

      {isTableView && (
        <div className={css.tableViewFiltersSection}>
          <div className={css.searchExportSection}>
            {tableSearch}
            {showExportButton && (
              <SecondaryButton
                title="Export"
                type="button"
                onClick={() => setModalOpen(true)}
                className={css.exportButtonTable}
              >
                <IconDownload />
              </SecondaryButton>
            )}
          </div>
          {tableViewSection}
        </div>
      )}

      <div className={css.searchOptions}>
        <div className={css.searchResultSummary}>
          <span className={css.resultsFound}>
            {searchInProgress ? (
              <FormattedMessage id="MainPanelHeader.loadingResults" />
            ) : resultsCount === null ? (
              ''
            ) : (
              <FormattedMessage
                id="MainPanelHeader.foundResults"
                values={{ count: resultsCount }}
              />
            )}
          </span>
        </div>
        {isSortByActive ? (
          <div className={css.sortyByWrapper}>
            <span className={css.sortyBy}>
              <FormattedMessage id="MainPanelHeader.sortBy" />
            </span>
            {sortByComponent}
          </div>
        ) : null}
      </div>

      {children}

      {noResultsInfo ? noResultsInfo : null}
    </div>
  );
};

MainPanelHeader.defaultProps = {
  rootClassName: null,
  className: null,
  resultsCount: null,
  searchInProgress: false,
  sortByComponent: null,
};

MainPanelHeader.propTypes = {
  rootClassName: string,
  className: string,
  listingsAreLoaded: bool.isRequired,
  resultsCount: number,
  searchInProgress: bool,
  sortByComponent: node,
};

export default MainPanelHeader;
